import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, FileText, Send, CheckCircle, XCircle, Clock, Upload, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const DemandeAbsenceJustifiee = ({ user, onSuccess }) => {
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demandes, setDemandes] = useState([]);
  
  // Formulaire
  const [date, setDate] = useState('');
  const [motif, setMotif] = useState('');
  const [justificatif, setJustificatif] = useState(null);
  const [justificatifName, setJustificatifName] = useState('');

  useEffect(() => {
    loadDemandes();
  }, []);

  const loadDemandes = async () => {
    try {
      const response = await axios.get(`/demandes-absence/${user.user_id}`);
      setDemandes(response.data || []);
    } catch (error) {
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setJustificatifName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        setJustificatif(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !motif) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/demandes-absence', {
        stagiaire_id: user.user_id,
        date,
        motif,
        justificatif,
        justificatif_name: justificatifName
      });
      
      toast.success('Demande envoyée au Centre');
      setShowForm(false);
      setDate('');
      setMotif('');
      setJustificatif(null);
      setJustificatifName('');
      loadDemandes();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error("Erreur lors de l'envoi de la demande");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (statut) => {
    switch (statut) {
      case 'acceptee':
        return (
          <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
            <CheckCircle className="h-4 w-4" /> Acceptée
          </span>
        );
      case 'refusee':
        return (
          <span className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full">
            <XCircle className="h-4 w-4" /> Refusée
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 text-sm font-medium rounded-full">
            <Clock className="h-4 w-4" /> En attente
          </span>
        );
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="demande-absence-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-orange-100 p-3 rounded-lg">
            <Calendar className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <h2 className="text-xl font-display font-semibold text-[#0F172A]">Demande d'Absence Justifiée</h2>
            <p className="text-sm text-slate-600">Soumettre une demande au Centre</p>
          </div>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="btn-nouvelle-demande"
        >
          {showForm ? 'Annuler' : 'Nouvelle demande'}
        </Button>
      </div>

      {/* Formulaire de demande */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-6" data-testid="form-demande-absence">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                Date de l'absence *
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                data-testid="input-date-absence"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <FileText className="h-4 w-4 inline mr-1" />
                Justificatif (optionnel)
              </label>
              <div className="relative">
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  data-testid="input-justificatif"
                />
                <div className="flex items-center gap-2 h-10 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white cursor-pointer hover:border-orange-400">
                  <Upload className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-600 truncate">
                    {justificatifName || 'Choisir un fichier...'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Motif de l'absence *
            </label>
            <textarea
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              required
              rows={3}
              placeholder="Décrivez le motif de votre absence..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
              data-testid="input-motif"
            />
          </div>
          
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="btn-submit-demande"
            >
              <Send className="h-4 w-4 mr-2" />
              {loading ? 'Envoi en cours...' : 'Envoyer la demande'}
            </Button>
          </div>
        </form>
      )}

      {/* Liste des demandes */}
      <div className="space-y-3">
        {demandes.length === 0 ? (
          <div className="text-center py-8 bg-slate-50 rounded-lg">
            <Calendar className="h-10 w-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500">Aucune demande d'absence</p>
          </div>
        ) : (
          demandes.map((demande) => (
            <div 
              key={demande.demande_id} 
              className={`p-4 rounded-lg border ${
                demande.statut === 'acceptee' ? 'bg-green-50 border-green-200' :
                demande.statut === 'refusee' ? 'bg-red-50 border-red-200' :
                'bg-white border-slate-200'
              }`}
              data-testid={`demande-${demande.demande_id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-semibold text-[#0F172A]">
                      {new Date(demande.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                    {getStatusBadge(demande.statut)}
                  </div>
                  <p className="text-sm text-slate-600 mb-2">{demande.motif}</p>
                  
                  {/* Réponse du Centre */}
                  {demande.statut !== 'en_attente' && demande.reponse_motif && (
                    <div className={`mt-3 p-3 rounded-lg ${
                      demande.statut === 'acceptee' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      <p className="text-sm font-medium text-slate-700">
                        <AlertCircle className="h-4 w-4 inline mr-1" />
                        Réponse du Centre :
                      </p>
                      <p className="text-sm text-slate-600 mt-1">{demande.reponse_motif}</p>
                    </div>
                  )}
                </div>
                
                {demande.justificatif && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = demande.justificatif;
                      link.download = demande.justificatif_name || 'justificatif';
                      link.click();
                    }}
                    className="shrink-0"
                    data-testid="btn-voir-justificatif"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Justificatif
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DemandeAbsenceJustifiee;
